import { Group, Modal, rem, Select, Stack, Textarea } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconCheck } from '@tabler/icons-react';
import Button from 'components/Button';
import UploadFile from 'components/files/UploadFile';
import { DateInput } from 'components/inputs/DateInput';
import P4Medium from 'components/typography/P4Medium';
import P4Regular from 'components/typography/P4Regular';
import { useMemo } from 'react';

export type HrBulkTermAction = 'execute' | 'set-due-date' | 'archive' | 'reactivate';

export interface HrBulkTermActionFormValues {
  action: HrBulkTermAction;
  executedOn: string;
  nextDueDate: string;
  dueDate: string;
  note: string;
  proofFileId: string;
  proofFileName: string;
  proofCategoryId: string;
}

interface HrBulkTermActionModalProps {
  opened: boolean;
  title: string;
  terms: any[];
  fileCategories: any[];
  action: HrBulkTermAction;
  onClose: () => void;
  onSubmit: (values: HrBulkTermActionFormValues) => void | Promise<void>;
}

/** Displays the exact applicable HR terms using the compact revision bulk-list presentation. */
function HrBulkTermList({ terms }: { terms: any[] }) {
  const style = useMemo(
    () => ({
      border: '1px solid #E9ECEF',
      borderRadius: '8px',
      backgroundColor: '#F8F9FA',
      padding: rem(12),
      paddingBottom: rem(8),
    }),
    []
  );

  return (
    <Stack spacing={4} style={style} aria-label="Termíny zahrnuté do operácie">
      {terms.map((term) => (
        <Group key={term.termId} spacing={8} noWrap align="start">
          <P4Medium color="gray.6">#{term.termId}</P4Medium>
          <P4Regular color="gray.7" lineClamp={2}>
            {term.termTypeName} – {term.firstName} {term.lastName}
          </P4Regular>
        </Group>
      ))}
    </Stack>
  );
}

/** Modal-contained form for one bulk HR-term operation. */
export default function HrBulkTermActionModal({
  opened,
  title,
  terms,
  fileCategories,
  action,
  onClose,
  onSubmit,
}: HrBulkTermActionModalProps) {
  const form = useForm<HrBulkTermActionFormValues>({
    initialValues: {
      action,
      executedOn: '',
      nextDueDate: '',
      dueDate: '',
      note: '',
      proofFileId: '',
      proofFileName: '',
      proofCategoryId: '',
    },
  });

  return (
    <Modal opened={opened} onClose={onClose} title={title}>
      <form onSubmit={form.onSubmit(onSubmit)}>
        <Stack>
          <HrBulkTermList terms={terms} />
          {form.values.action === 'execute' && (
            <>
              <DateInput size="md" label="Dátum vykonania" withAsterisk {...form.getInputProps('executedOn')} />
              <DateInput
                size="md"
                label="Nasledujúci dátum (ak sa má prepísať)"
                {...form.getInputProps('nextDueDate')}
              />
            </>
          )}
          {(form.values.action === 'set-due-date' || form.values.action === 'reactivate') && (
            <DateInput size="md" label="Nový termín" withAsterisk {...form.getInputProps('dueDate')} />
          )}
          <Textarea size="md" label="Spoločná poznámka" {...form.getInputProps('note')} />
          {form.values.action === 'execute' && (
            <>
              <UploadFile
                label="Spoločná príloha"
                value={form.values.proofFileId}
                onChange={(value) => form.setFieldValue('proofFileId', value)}
                onFileProcessed={(file) => form.setFieldValue('proofFileName', file?.name ?? '')}
              />
              {form.values.proofFileId && (
                <Select
                  size="md"
                  label="Kategória spoločnej prílohy"
                  withAsterisk
                  data={fileCategories.map((category) => ({
                    value: String(category.categoryId),
                    label: category.name ?? `Kategória ${category.categoryId}`,
                  }))}
                  {...form.getInputProps('proofCategoryId')}
                />
              )}
            </>
          )}
          <Group position="right" mt="md">
            <Button variant="subtle-gray" type="button" onClick={onClose}>
              Zrušiť
            </Button>
            <Button variant="primary" type="submit" leftIcon={<IconCheck stroke={1.5} />}>
              {form.values.action === 'archive'
                ? 'Vyradiť'
                : form.values.action === 'reactivate'
                ? 'Zaradiť'
                : 'Potvrdiť'}{' '}
              ({terms.length})
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
